import { useEffect, useState } from 'preact/hooks'
import { ListChecks, Lock, Plus } from 'lucide-preact'
import { websApi, type QuizDto, type WebViewDto } from '@/api/webs'
import type { QuizDraftDto } from '@/types/api-generated'
import { ConfirmationModal } from '@/components'
import { ChatEntityTypes, ChatLocations, ChatViewModes } from '@/constants/aiChat'
import { useAIForm } from '@/hooks/useAIForm'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { combineTouched, mergeQuizDraft, NOTHING_TOUCHED, QUIZ_AI_FIELD, type QuizDraftTouched } from '@/utils/quizDraft'
import { QuizReadView } from './QuizReadView'
import { QuizEditor, type QuizFormValues } from './QuizEditor'
import { QuizHeading } from './QuizHeading'

interface Props {
    web: WebViewDto
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
    isSignedOff: boolean
}

/**
 * What Add a quiz opens the editor on when no version is live. With one live, it opens on that version instead, so a
 * quiz removed by mistake can come back with the IDs people's answers point at.
 */
const NEW_QUIZ: QuizDto = { ScaleMax: 10, DetailsPlacement: 'After', Questions: [], Dimensions: [], Bands: [], ButtonLabel: null, ButtonUrl: null, ReportSections: [], Disclaimer: null }

/** A deep copy, so editing never writes into the web the page holds. */
const workingCopy = (quiz: QuizDto): QuizDto => JSON.parse(JSON.stringify(quiz)) as QuizDto

/**
 * The Quiz tab on a Download web: the saved quiz, its editor, and the empty state. The server owns
 * every rule — the save checks, the answers lock and the sign-off readiness — so a refused save shows
 * the server's message and the editor stays open.
 *
 * Webster reads the working quiz through one AI field, Quiz, editable only while the editor is open
 * (ORB-926). Its drafts arrive through that field's handler and are merged here; nothing Webster does
 * saves the quiz.
 */
export function QuizPanel({ web, onPatchWeb, isSignedOff }: Props) {
    const quiz = web.Download?.Quiz ?? null
    const { showSuccess, showError, showErrorUnless422 } = useToast()
    const { canEdit } = useWorkspaceCapabilities()
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [confirmingRemove, setConfirmingRemove] = useState(false)
    const [suggested, setSuggested] = useState<QuizDraftTouched>(NOTHING_TOUCHED)

    const { form } = useAIForm<QuizFormValues>({ mode: 'onChange', defaultValues: { quiz: quiz ? workingCopy(quiz) : NEW_QUIZ } }, {
        aiContext: { campaignId: web.CampaignId, webId: web.Id, location: ChatLocations.WebQuizEditor, entityType: ChatEntityTypes.Web, entityId: web.Id, viewMode: editing ? ChatViewModes.Edit : ChatViewModes.View },
        fields: [{ aiName: QUIZ_AI_FIELD, formPath: 'quiz', displayName: 'Quiz', fieldType: 'textarea', isEditable: editing }],
        loadedData: { quizHasAnswers: (web.Download?.QuizSubmissionCount ?? 0) > 0 },
        onFieldUpdate: (aiName, value) => {
            if (aiName === QUIZ_AI_FIELD) putDraftInEditor(JSON.parse(value) as QuizDraftDto)
        },
    })

    // Out of the editor, Webster reads the quiz as saved, not whatever a cancelled edit left in the form.
    useEffect(() => {
        if (!editing) form.reset({ quiz: quiz ? workingCopy(quiz) : NEW_QUIZ })
    }, [editing, quiz])

    /**
     * Set list by list rather than setValue('quiz', …): a field array with no inputs registered yet (an empty quiz)
     * is only refreshed when its own path is set.
     */
    const putDraftInEditor = (draft: QuizDraftDto) => {
        // The card's button is greyed while the editor is closed; this stops a stale click from writing a closed form.
        if (!editing) throw new Error('The Quiz editor is closed.')
        if (draft.WebId !== web.Id) throw new Error('The draft was checked against another web.')
        const { quiz: merged, touched } = mergeQuizDraft(form.getValues('quiz'), draft)
        form.setValue('quiz.Dimensions', merged.Dimensions, { shouldDirty: true })
        form.setValue('quiz.Questions', merged.Questions, { shouldDirty: true })
        form.setValue('quiz.Bands', merged.Bands, { shouldDirty: true })
        form.setValue('quiz.ReportSections', merged.ReportSections, { shouldDirty: true })
        form.setValue('quiz.Disclaimer', merged.Disclaimer ?? null, { shouldDirty: true })
        setSuggested(previous => combineTouched(previous, touched))
    }

    const openEditor = (start: QuizDto) => {
        form.reset({ quiz: workingCopy(start) })
        setSuggested(NOTHING_TOUCHED)
        setEditing(true)
    }

    const closeEditor = () => {
        setSuggested(NOTHING_TOUCHED)
        setEditing(false)
    }

    const persist = async (next: QuizDto | null, successMessage: string) => {
        setSaving(true)
        try {
            const res = await websApi.updateDownloadQuiz(web.WorkspaceId, web.Id, { ETag: web.ETag, Quiz: next })
            if (!res.error && res.data) {
                onPatchWeb(prev => ({ ...prev, ...res.data }))
                showSuccess('Updated', successMessage)
                closeEditor()
                return true
            }
            showErrorUnless422('Save failed', res, 'Unable to save the quiz')
        } catch {
            showError('Save failed', 'Network or server error')
        } finally {
            setSaving(false)
        }
        return false
    }

    const save = form.handleSubmit(values => persist(values.quiz, 'Quiz saved.'))
    const remove = async () => {
        await persist(null, 'Quiz removed.')
        setConfirmingRemove(false)
    }

    const signedOffQuiz = web.Download?.SignedOffQuiz ?? null

    if (editing) return (
        <>
            <QuizEditor
                web={web}
                form={form}
                saving={saving}
                onSave={save}
                onCancel={closeEditor}
                onRemove={() => setConfirmingRemove(true)}
                onGoBackToSignedOff={() => { if (signedOffQuiz) openEditor(signedOffQuiz) }}
                suggested={suggested}
            />
            <ConfirmationModal
                isOpen={confirmingRemove}
                title="Remove the quiz?"
                message={web.Download?.SignedOffQuizVersion != null
                    ? 'The questions, dimensions, bands and results copy are deleted. The live page keeps the signed-off version until the web is signed off again.'
                    : 'The questions, dimensions, bands and results copy are deleted.'}
                type="danger"
                confirmText="Remove quiz"
                loading={saving}
                onConfirm={remove}
                onCancel={() => setConfirmingRemove(false)}
            />
        </>
    )

    if (quiz) return <QuizReadView web={web} quiz={quiz} canEdit={canEdit} isSignedOff={isSignedOff} onEdit={() => openEditor(quiz)} />

    // After a removal the signed-off version stays live until the next sign-off, so the heading says which one.
    return (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            {web.Download?.SignedOffQuizVersion != null
                ? <QuizHeading web={web} />
                : (
                    <div className="px-6 pt-6">
                        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 dark:text-white"><ListChecks className="w-5 h-5 text-primary-600" /> Quiz</h2>
                    </div>
                )}
            <div className="px-6 pt-8 pb-14 flex flex-col items-center text-center">
                <ListChecks className="w-10 h-10 text-slate-400 dark:text-slate-500" />
                <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">No quiz yet</p>
                <p className="mt-1.5 text-sm text-slate-500 max-w-[56ch] dark:text-slate-400">Ask questions before people get the download, score their answers out of 100, and show each person their level, what it means and what to do next.</p>
                {canEdit && (isSignedOff
                    ? <p className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400"><Lock className="w-4 h-4" />Reopen the web to add a quiz</p>
                    : (
                        <>
                            <button type="button" onClick={() => openEditor(signedOffQuiz ?? NEW_QUIZ)} className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"><Plus className="w-4 h-4" />Add a quiz</button>
                            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">It goes live when the web is next signed off.</p>
                        </>
                    ))}
            </div>
        </section>
    )
}
